/**
 * The English dictionary.
 *
 * ⚠️ **Le vocabulaire du jeu reste en français.** Zinc, nénette, rampeau, cave,
 * recave, sec, 421 : c'est le nom des choses, pas du texte d'interface. Un joueur
 * anglais qui apprend le 421 apprend « le sec » — « the dry throw » n'existe pas et
 * ne veut rien dire. On traduit la prose autour, jamais les termes.
 *
 * `Textes` est la forme de `fr.ts` : une clé manquante, une clé en trop ou une arité
 * qui change ne compile pas. Et `tests/i18n.test.ts` exige en plus que chaque entrée
 * DIFFÈRE du français, sauf celles que `VOCABULAIRE_COMMUN` dispense — parce que le
 * compilateur, lui, accepte parfaitement un dictionnaire recopié.
 */

import type { Textes } from "./fr.js";

interface Lieu {
  readonly nom: string;
  readonly accroche: string;
}

const COMPTOIRS: Readonly<Record<string, Lieu>> = {
  gare: {
    nom: "The Station Café",
    accroche: "A customer in a hurry, a train to catch. He throws and he lays down.",
  },
  balto: {
    nom: "Le Balto",
    accroche: "Two regulars, a coffee machine, and nobody who ever stops.",
  },
  petitZinc: {
    nom: "The Little Zinc",
    accroche: "They play here absent-mindedly, between two orders. Make the most of it.",
  },
  chope: {
    nom: "The Tankard",
    accroche: "Four around the zinc: three chances that someone does worse than you.",
  },
  tabac: {
    nom: "The Sports Bar",
    accroche: "The high roller is chasing the 421. Get there before him.",
  },
  cercle: {
    nom: "The Circle",
    accroche: "Here they count. Five at the table, and a calculator who stops in time.",
  },
  arriereSalle: {
    nom: "The Back Room",
    accroche: "The boss plays from memory, and he talks about it. Don't listen.",
  },
  taulier: {
    nom: "The Landlord's Counter",
    accroche: "La cave, la recave, la cave des caves. One evening, and it's him.",
  },
};

const ADVERSAIRES: Readonly<Record<string, string>> = {
  presse: "The Hurried",
  acharne: "The Relentless",
  distrait: "The Distracted",
  habitue: "The Regular",
  flambeur: "The High Roller",
  calculateur: "The Calculator",
  patron: "The Boss",
  taulier: "The Landlord",
};

function manquant(cle: string): string {
  return `« ? ${cle} »`;
}

export const EN: Textes = {
  // ------------------------------------------------------------- le vocabulaire
  appNom: "Zinc 421",
  motSec: "SEC",
  motRampeau: "Rampeau",
  motNenette: "nénette",
  etapeCave: "La cave",
  etapeRecave: "La recave",
  etapeFinale: "La cave des caves",
  etapeFini: "Over",

  // ------------------------------------------------------------------ numbers
  jetons: (n: number) => `${n} chip${n > 1 ? "s" : ""}`,
  des: (n: number) => `${n} ${n > 1 ? "dice" : "die"}`,
  lancers: (n: number) => `${n} throw${n > 1 ? "s" : ""}`,
  manches: (n: number) => `${n} round${n > 1 ? "s" : ""}`,
  tentatives: (n: number) => `${n} attempt${n > 1 ? "s" : ""}`,

  // ------------------------------------------------------------------ content
  comptoirNom: (cle: string) => COMPTOIRS[cle]?.nom ?? manquant(cle),
  comptoirAccroche: (cle: string) => COMPTOIRS[cle]?.accroche ?? manquant(cle),
  adversaireNom: (cle: string) => ADVERSAIRES[cle] ?? manquant(cle),
  /** No contraction in English: "at The Balto" works as it is. */
  auComptoir: (lieu: string) => `at ${lieu}`,

  // ------------------------------------------------------------ combinations
  comboQuatreCentVingtEtUn: "421 !",
  comboBrelanAs: "three aces",
  comboBrelan: (hauteur: number) => `three ${hauteur}s`,
  comboPaireAs: "pair of aces",
  comboNenette: "nénette",
  comboSuite: "run",

  // -------------------------------------------------------------- home screen
  accueilAccroche: "The bar-counter 421. A sec doubles the stake.",
  combienATable: "How many at the table?",
  quiJoue: "Who's playing?",
  adversairesVirtuels: (n: number) => `${n} computer opponent${n > 1 ? "s" : ""}`,
  tableToutHumaine: "all human table",
  nomDuJoueur: (rang: number) => `Name of player ${rang}`,
  joueurNumero: (rang: number) => `Player ${rang}`,
  retirerJoueur: (nom: string) => `Remove ${nom}`,
  ajouterUnJoueur: "+ another player on this phone",
  enFace: "Facing you",
  perdTaux: (taux: string) => `loses ${taux}`,
  jouer: "Play",
  onPrepareLaTable: "Setting the table…",
  laTournee: "The bar crawl",
  jouerEnLigne: "Play online, one phone each",
  justeLancerLesDes: "Just throw the dice",
  sonActif: "🔊 Sound and vibration: on",
  sonCoupe: "🔇 Sound and vibration: off",
  choisirLaLangue: "Language",
  reprendreLaPartie: "Resume game",
  repriseSoiree: "Evening in progress",
  repriseManche: (n: number) => `round ${n}`,

  // ---------------------------------------------------------------- the game
  enjeuCharge: "Charge — the weakest takes the chips from the pot.",
  enjeuDecharge: "Décharge — the strongest hands his chips to the weakest.",
  partieTerminee: "Game over.",
  masse: (jetons: string) => `Pot: ${jetons}`,
  mancheNumero: (n: number) => `round ${n}`,
  lesJoueurs: "The players",
  lesTroisDes: "The three dice",
  renommerJoueur: (nom: string) => `Rename ${nom}`,
  nouveauNom: "New name",
  valider: "Confirm",
  sauve: "safe",
  dejaPose: "Already laid down, strongest first",
  deAria: (rang: number, quoi: string) => `Die ${rang}: ${quoi}`,
  deEnLAir: "in the air",
  deGarde: (valeur: number) => `${valeur}, kept`,
  lancerLesDes: "🎲 Throw the dice",
  relancerDes: (n: number) => `🎲 Rethrow ${n} ${n > 1 ? "dice" : "die"}`,
  jeMArrete: "I'll stop there",
  unConseil: "Any advice?",
  demanderConseil: "Ask for advice",
  cEstFini: "That's it",
  finDePartie: "End of game",
  paieSaTournee: "buys the round",
  quitter: "Leave",
  couperLeSon: "Mute",
  remettreLeSon: "Unmute",
  qualiteReduite: "Reduced quality (low fps)",
  contextePerdu: "The 3D display was interrupted by the system. The game is safe; "
    + "the scene will come back shortly.",

  // ------------------------------------------------------------- the prompt
  desQuiRoulent: "The dice are rolling…",
  paieSaTourneePhrase: (nom: string) => `${nom} buys the round.`,
  aQui: (nom: string, suite: string) => `${nom} — ${suite}`,
  rampeauLance: (nom: string) => `Rampeau — ${nom} throws.`,
  rampeauUnLancer: "rampeau, a single throw to settle it.",
  ilReflechit: (nom: string) => `${nom} is thinking…`,
  aToiDOuvrir: "you open. Stopping now leaves everyone a single throw.",
  aToi: (suite: string) => `your turn. ${suite}`,
  mancheSeche: "This round is sec: a single throw.",
  lancersAuMaximum: (n: number) => `${n} throws at most.`,
  plusDeRelance: "no throw left: you lay down what you have.",
  gardeOuRelanceFixe: (n: number) =>
    `keep what suits you, or throw again. Stopping would fix the round at ${n} `
    + `throw${n > 1 ? "s" : ""}.`,
  gardeOuRelanceReste: (n: number) =>
    `keep what suits you, or throw again — ${n} throw${n > 1 ? "s" : ""} left.`,

  // ---------------------------------------------------------- handing over
  passerLAppareil: "Hand the phone over",
  passerLAppareilAria: "Hand the phone over",
  cEstMoi: "That's me",

  // ----------------------------------------------------------- announcements
  annonce421: (nom: string, moi: boolean) =>
    (moi ? "421! You sweep the stake." : `421! ${nom} sweeps the stake.`),
  annonceNenette: (nom: string, moi: boolean) =>
    (moi ? "The nénette for you — the weakest hand there is."
      : `The nénette for ${nom} — the weakest hand there is.`),
  annonceSec: "SEC! A single throw for everyone, and the stake doubles.",
  annonceRampeau: (noms: string, enjeu: string) =>
    `Rampeau — ${noms}. One throw each to find out ${enjeu}.`,
  rampeauQuiRamasse: "who takes",
  rampeauQuiDonne: "who gives",
  rampeauQuiRecoit: "who receives",
  annonceRamasse: (nom: string, jetons: string, moi: boolean) =>
    (moi ? `You take ${jetons}.` : `${nom} takes ${jetons}.`),
  miseDoublee: "The stake was doubled: the round was sec.",
  misePotVide: "That was all that was left in the pot.",
  annonceDecharge: "DÉCHARGE! The pot is empty: from now on you must get rid of "
    + "your chips.",
  annonceSauve: (nom: string, moi: boolean) =>
    (moi ? "You're out of trouble." : `${nom} is out of trouble.`),
  annonceFin: (nom: string, moi: boolean) =>
    (moi ? "You're buying the round." : `${nom} buys the round.`),

  // -------------------------------------------------------------- the coach
  coachPoser: (main: string) => `No throw left: you lay down ${main}.`,
  coachArreter: (main: string) => `Stop there: ${main} is enough.`,
  coachToutRelancer: (main: string) => `Throw everything again, ${main} is worthless.`,
  coachGarder: (gardes: string, des: string) => `Keep ${gardes}, rethrow ${des}.`,
  coachDe: (valeur: number) => `the ${valeur}`,
  coachEtPuis: (debut: string, dernier: string) => `${debut} and ${dernier}`,
  coachEnjeuCharge: "In charge, the weakest takes the stake.",
  coachEnjeuDecharge: "In décharge, you must be the STRONGEST to give.",
  coachObjectif: (main: string, sur: number) =>
    `You have to beat ${main} — a fresh throw manages it once in ${sur}.`,
  coachObjectifJamais: (main: string) =>
    `You have to beat ${main} — a fresh throw never manages it.`,
  coachObjectifToujours: (main: string) =>
    `You have to beat ${main} — almost every throw manages it.`,

  // ------------------------------------------------------------ the campaign
  campagneTitre: "The bar crawl",
  avancement: (gagnes: number, total: number, etoiles: number) =>
    `${gagnes} of ${total} bar${total > 1 ? "s" : ""}`
    + ` · ${etoiles} star${etoiles > 1 ? "s" : ""}`,
  tourneeToutesEtoiles: "The whole crawl, and every star. There is nobody left to "
    + "beat.",
  tourneeFinie: (etoiles: number, total: number) =>
    `The whole crawl is done — ${etoiles} star${etoiles > 1 ? "s" : ""} out of `
    + `${total}. The missing challenges are waiting for you.`,
  tonNomAuComptoir: "Your name at the counter",
  tonNomPlaceholder: "You",
  comptoirVerrouille: "Win the previous bar to get in.",
  comptoirATable: (joueurs: number, adversaires: string) =>
    `${joueurs} at the table: ${adversaires}`,
  comptoirSoiree: " · full evening: cave, recave, cave des caves",
  comptoirDefi: (defi: string) => `Challenge: ${defi}`,
  comptoirGagne: "won",
  defiReussiAria: "challenge completed",
  depuisLeDebut: "Since the beginning",
  tesStatistiques: "Your statistics",
  revenirAuComptoir: "Back to the counter",
  stockageMuet: "This device refuses to save: the crawl will not be remembered.",

  // ----------------------------------------------------------- the challenges
  defiAvec421: "Win with a 421",
  defiAvecSec: "Win with at least one sec",
  defiSansRampeau: "Win without a single rampeau",
  defiEnMoinsDe: (manches: number) => `Win in ${manches} rounds or fewer`,
  defiAuPlus: (jetons: number) => `Win taking ${jetons} chips at most`,

  // ------------------------------------------------------------- the outcome
  issueGagneEtoile: (lieu: string) => `★ ${lieu} is yours, and the challenge with it.`,
  issueGagne: (lieu: string, defi: string) =>
    `${lieu} is yours. Challenge missed: ${defi.charAt(0).toLowerCase()}${defi.slice(1)}`,
  issuePerdu: (auLieu: string) => `You're buying the round ${auLieu}. Go again?`,
  retourALaTournee: "Back to the crawl",
  enchainerLaRecave: "Carry on: la recave",
  laSuite: "What comes next",
  voirQuiPaie: "See who pays",
  uneAutreSoiree: "Another evening?",

  // ------------------------------------------------------------ the statistics
  statParties: "Games played",
  statGagnees: "Won",
  statPourcentage: (valeur: number, pourcent: number) => `${valeur} (${pourcent}%)`,
  statTournees: "Rounds bought",
  statManches: "Rounds played",
  stat421: "421s hit",
  statNenettes: "Nénettes taken",
  statSecs: "Secs called",
  statRampeaux: "Rampeaux fought",
  statCharge: "Pickups in charge",
  statDecharge: "Handovers in décharge",
  statJetons: "Chips taken",
  statMeilleurCoup: "Best hand",

  // ---------------------------------------------------------------- online
  enLigneTitre: "Play online",
  enLigneAccroche: "One phone each, one code to share.",
  tonNomATable: "Your name at the table",
  tonNom: "Your name",
  ouvrirUnePartie: "Open a table",
  onOuvreLaTable: "Opening the table…",
  ouRejoindreAvecUnCode: "Or join with a code",
  codeDeLaPartie: "Table code",
  codeRecu: "code you were given",
  rejoindre: "Join",
  salleDAttente: "Waiting room",
  partageCeCode: "Share this code, or fill up with opponents.",
  lesJoueursPresents: "Players present",
  joueurVirtuel: "computer",
  joueurEnLigne: "online",
  completerAvecUnVirtuel: "Fill a seat with a computer opponent",
  aLHoteDeLancer: "Whoever opened the table starts the game.",
  lancerLaPartie: "Start the game",
  joueurDeconnecte: "disconnected",
  joueurRemplace: "replaced",
  cEstToi: " (you)",
  affichageRattrape: " · display caught up",
  tableNumero: (code: string) => `Table ${code}`,

  niveauNumero: (n: number) => `level ${n}`,
  encoreDesJoueurs: (n: number) => `${n} more player${n > 1 ? "s" : ""} needed`,
  commencerLaCave: "Start la cave",
  quitterLaTable: "Leave the table",
  toutRelacher: "Release everything",
  retour: "Back",
  combinaisonEtJetons: (nom: string, jetons: string) => `${nom} — ${jetons}`,

  // ------------------------------------------------------------- the sandbox
  bacLanceLesDes: "Throw the three dice.",
  bacTuGardesTout: "You're keeping everything — that's your result.",
  bacToucheUnDe: "Tap a die to keep it, then throw again.",
  bacPasEncoreLance: "not thrown yet",
  bacMoteurNomme: "The shared rules engine names the combination",
};
